import { ISymbolDetail } from '@aqx-core/common/api/market';
import { EAlertCondition, EAlertFrequency, IPriceAlert } from '@aqx-core/common/api/price-alert';
import {
  DEFAULT_POINTS_STEP,
  EPriceAlertTab,
  MAX_ALERTS_FOR_ALL_SYMBOLS,
  MAX_ALERTS_PER_SYMBOL
} from '@aqx-core/common/constants/price-alert';
import { IChangeColors } from '@aqx-core/common/constants/styling';
import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { i18n } from '@aqx-core/common/i18n';

import { getDecimalForCompanyIntegration } from '../price/getDecimalFromPrice';
import { formatPercentageDiffValue, formatPriceValue } from '../price/valueFormatters';

export const getAlertConditionLabel = (condition: EAlertCondition): string => {
  const ALERT_CONDITION_LABEL_MAP: {
    [key in EAlertCondition]: () => string;
  } = {
    [EAlertCondition.ABOVE]: () => i18n.t('Price more than (≥)'),
    [EAlertCondition.BELOW]: () => i18n.t('Price less than (≤)'),
    [EAlertCondition.CROSS_ABOVE]: () => i18n.t('Price rises to (≥)'),
    [EAlertCondition.CROSS_BELOW]: () => i18n.t('Price falls to (≤)'),
    [EAlertCondition.POSITIVE_DAILY_CHANGE_REACHES]: () => i18n.t('Positive daily change (%) reaches'),
    [EAlertCondition.NEGATIVE_DAILY_CHANGE_REACHES]: () => i18n.t('Negative daily change (%) reaches')
  };
  return ALERT_CONDITION_LABEL_MAP[condition]();
};

export const getAlertFrequencyLabel = (frequency: EAlertFrequency): string => {
  const ALERT_FREQUENCY_LABEL_MAP: {
    [key in EAlertFrequency]: () => string;
  } = {
    [EAlertFrequency.ONLY_ONCE]: () => i18n.t('Only Once'),
    [EAlertFrequency.PER_DAY]: () => i18n.t('Once Per Day'),
    [EAlertFrequency.ALWAYS]: () => i18n.t('Always')
  };
  return ALERT_FREQUENCY_LABEL_MAP[frequency]();
};

export const getAlertLimitMsg = (
  maxAlertsPerSymbol: number = MAX_ALERTS_PER_SYMBOL,
  maxAlertsInTotal: number = MAX_ALERTS_FOR_ALL_SYMBOLS
) => {
  return i18n.t(
    'You may only create up to {{maxAlertsPerSymbol}} alerts for each symbol and up to {{maxAlertsInTotal}} total alerts for all symbols.',
    { maxAlertsPerSymbol, maxAlertsInTotal }
  );
};

export const getManageAlertsMsg = () => {
  return i18n.t(
    'You can select multiple alerts to be deleted. Deleted alerts will be removed permanently. You will no longer receive notifications from deleted price alerts.'
  );
};

export const getManagePriceAlertTitle = (currentTab: EPriceAlertTab) => {
  return currentTab === EPriceAlertTab.CURRENT_SYMBOL
    ? i18n.t('Manage Price Alerts')
    : i18n.t('Manage All Price Alerts');
};

export const getTargetValueIncrementDataTestId = () => {
  return `${EDataTestId.PRICE_ALERT_MANAGEMENT_TARGET_VALUE_INPUT}-increase`;
};

export const getTargetValueDecrementDataTestId = () => {
  return `${EDataTestId.PRICE_ALERT_MANAGEMENT_TARGET_VALUE_INPUT}-decrease`;
};

export const getDeleteAlertsModalContent = (
  noOfAlerts: number
): {
  title: string;
  description: string;
} => {
  const hasMoreThanOneAlert = noOfAlerts > 1;
  const title = hasMoreThanOneAlert
    ? i18n.t('Delete {{noOfAlerts}} Price Alerts', { noOfAlerts })
    : i18n.t('Delete Price Alert');
  const description = hasMoreThanOneAlert
    ? i18n.t('This action will delete the selected price alerts and cannot be undone.')
    : i18n.t('This action will delete the price alert and cannot be undone.');
  return { title, description };
};

export const getSymbolDisabledMsg = () => {
  return i18n.t(
    'This symbol has been temporarily disabled. Your existing alerts for this symbol will remain inactive until the symbol is re-enabled.'
  );
};

export const getPriceAlertDirectionColor = (condition: EAlertCondition, colors: IChangeColors): string => {
  switch (condition) {
    case EAlertCondition.BELOW:
    case EAlertCondition.CROSS_BELOW:
    case EAlertCondition.NEGATIVE_DAILY_CHANGE_REACHES:
      return colors.negative;
    case EAlertCondition.ABOVE:
    case EAlertCondition.CROSS_ABOVE:
    case EAlertCondition.POSITIVE_DAILY_CHANGE_REACHES:
    default:
      return colors.positive;
  }
};

export const getFormattedTargetValue = (
  priceAlertInfo: Pick<IPriceAlert, 'condition' | 'targetPrice'>,
  targetSymbolDetail: Pick<ISymbolDetail, 'decimal' | 'tickRulesGroup'>
) => {
  const { condition, targetPrice } = priceAlertInfo;
  const { decimal, tickRulesGroup } = targetSymbolDetail;
  switch (condition) {
    case EAlertCondition.BELOW:
    case EAlertCondition.CROSS_BELOW:
    case EAlertCondition.ABOVE:
    case EAlertCondition.CROSS_ABOVE:
      return formatPriceValue(targetPrice, tickRulesGroup, decimal);
    case EAlertCondition.POSITIVE_DAILY_CHANGE_REACHES:
    case EAlertCondition.NEGATIVE_DAILY_CHANGE_REACHES:
    default:
      return formatPercentageDiffValue(targetPrice);
  }
};

export const isPercentageCondition = (condition?: EAlertCondition): boolean => {
  return Boolean(
    condition &&
      [EAlertCondition.POSITIVE_DAILY_CHANGE_REACHES, EAlertCondition.NEGATIVE_DAILY_CHANGE_REACHES].includes(condition)
  );
};

interface IPriceFormattingInfoProps {
  decimal?: ISymbolDetail['decimal'];
  isPercentCondition: boolean;
  pointStep?: ISymbolDetail['pointStep'];
  tickRulesGroup?: ISymbolDetail['tickRulesGroup'];
}

interface IPriceFormattingInfo {
  decimalsByIntegration: number;
  pointStep: number;
}

export const getPriceFormattingInfo = (props: IPriceFormattingInfoProps): IPriceFormattingInfo => {
  const { decimal, isPercentCondition, pointStep, tickRulesGroup } = props;
  if (!decimal || !pointStep || isPercentCondition) {
    const pointStep = isPercentCondition ? 0.01 : DEFAULT_POINTS_STEP;
    return {
      decimalsByIntegration: 2,
      pointStep
    };
  }

  const decimalsByIntegration = getDecimalForCompanyIntegration(pointStep, tickRulesGroup, decimal);
  return {
    decimalsByIntegration,
    pointStep
  };
};
